import { useMemo, useState } from 'react'
import { Alert, Button, List } from '@/ui'
import { SyncErrorClassification } from '@/actions/plaid/authActions'
import { SYNC_ERROR_MESSAGES } from '@/components/plaid/syncErrorMessages'
import type { ResolvedPlaidItemSyncStatus } from './bankAccounts.helpers'
import { useSyncStatusContext } from './SyncStatusContext'
import { usePlaidRecovery } from './usePlaidRecovery'

/**
 * Renders `message` with `actionWord` swapped for a link, so the copy's own verb
 * is the control ("Please <retry> again later").
 */
const MessageWithInlineAction = ({
  message,
  actionWord,
  onAction,
}: {
  message: string
  actionWord: string
  onAction: () => void
}) => {
  const start = message.indexOf(actionWord)
  if (start === -1) return message

  return (
    <>
      {message.slice(0, start)}
      <Button
        variant="link"
        className="p-0 h-auto align-baseline"
        onClick={onAction}
      >
        {actionWord}
      </Button>
      {message.slice(start + actionWord.length)}
    </>
  )
}

interface Recovery {
  label: string
  run: () => void
}

const FinancialInstitutionsErrorCard = ({
  classification,
  items,
  onDismiss,
}: {
  classification: SyncErrorClassification
  items: ResolvedPlaidItemSyncStatus[]
  onDismiss: () => void
}) => {
  const { reconnect, retry } = usePlaidRecovery()
  const message = SYNC_ERROR_MESSAGES[classification]
  const { actionWord } = message

  /**
   * The recovery this classification offers for one item, as a label and the
   * handler that runs it — kept together so the two cannot drift apart. Null
   * when the classification offers nothing, or when a reconnect has no account
   * to target.
   */
  const getRecovery = (item: ResolvedPlaidItemSyncStatus): Recovery | null => {
    switch (message.action) {
      case 'retry':
        return { label: 'Retry', run: () => retry(item.plaidItemId) }
      case 'reconnect': {
        const accountId = item.accounts[0]?.id
        return accountId
          ? { label: 'Reconnect', run: () => reconnect(accountId) }
          : null
      }
      default:
        return null
    }
  }

  const renderBullet = (item: ResolvedPlaidItemSyncStatus) => {
    const recovery = getRecovery(item)

    return (
      <List.Item key={item.plaidItemId}>
        <b>{item.institutionName ?? 'Your bank'}</b>:{' '}
        {recovery && actionWord ? (
          <MessageWithInlineAction
            message={message.message}
            actionWord={actionWord}
            onAction={recovery.run}
          />
        ) : (
          <>
            {message.message}
            {recovery && (
              <>
                {' '}
                <Button
                  variant="link"
                  className="p-0 h-auto"
                  onClick={recovery.run}
                >
                  {recovery.label}
                </Button>
              </>
            )}
          </>
        )}
      </List.Item>
    )
  }

  const bullets = items.map(renderBullet)

  return (
    <Alert
      variant="error"
      title={message.title}
      dismissible
      onClose={onDismiss}
    >
      <List bulleted>{bullets}</List>
    </Alert>
  )
}

export const FinancialInstitutionsError = () => {
  const { plaidItems: items } = useSyncStatusContext()
  const [dismissedItemIds, setDismissedItemIds] = useState<number[]>([])

  const errorGroups = useMemo(() => {
    const grouped = new Map<
      SyncErrorClassification,
      ResolvedPlaidItemSyncStatus[]
    >()
    items.forEach((item) => {
      const classification = item.resolvedErrorClassification
      if (!classification) return
      grouped.set(classification, [
        ...(grouped.get(classification) ?? []),
        item,
      ])
    })
    return Array.from(grouped, ([classification, classificationItems]) => ({
      classification,
      items: classificationItems,
    }))
  }, [items])

  const visibleGroups = errorGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => !dismissedItemIds.includes(item.plaidItemId)
      ),
    }))
    .filter((group) => group.items.length > 0)

  if (visibleGroups.length === 0) return null

  const cards = visibleGroups.map((group) => (
    <FinancialInstitutionsErrorCard
      key={group.classification}
      classification={group.classification}
      items={group.items}
      onDismiss={() =>
        setDismissedItemIds((prev) => [
          ...prev,
          ...group.items.map((item) => item.plaidItemId),
        ])
      }
    />
  ))

  return <div className="flex flex-col gap-2 mb-6">{cards}</div>
}
